/* Qik Joe — local conditions

   Reads a forecast for wherever the visitor is and hands the low temperature
   to the temperature finder, so "pick your cold" starts on the cold they are
   actually getting tonight.

   Data comes from Open-Meteo: no API key, no account, no tracking pixel, and
   nothing secret that would have to live in the theme where anyone could read
   it. Geocoding for a typed town or ZIP uses the same service.

   Location handling, deliberately:
   - A first-time visitor is never prompted on load; the button does that.
     Browsers treat a dismissed prompt as a lasting no, so an unexplained
     dialog can cost the permission permanently. The merchant can override
     this in the theme editor.
   - Someone who has already allowed it is used automatically and silently on
     every later visit — that is the considerAutoLocate 'granted' branch.
   - A default location (the plant's own) renders immediately, so the widget is
     never empty and never blocks on a permission decision.
   - Coordinates are used for one request and are not stored or sent anywhere
     else. The chosen place name is kept in localStorage so a returning visitor
     does not have to ask twice; that is a place name, not a coordinate. */

(function () {
  'use strict';

  var IPGEO = 'https://ipwho.is/?fields=success,city,region,latitude,longitude';
  var GEO = 'https://geocoding-api.open-meteo.com/v1/search';
  var FORECAST = 'https://api.open-meteo.com/v1/forecast';
  var STORE_KEY = 'qj:place';
  var TIMEOUT = 8000;

  /* fetch() has no timeout of its own: a request that never answers leaves the
     promise pending forever, and neither .then nor .catch runs. That is how a
     widget ends up stuck on "Loading…" — which is exactly what a flaky mobile
     connection produces. Abort after TIMEOUT so failure is always reachable. */
  function fetchJSON(url) {
    if (typeof AbortController === 'undefined') return fetch(url).then(function (r) { return r.json(); });

    var controller = new AbortController();
    var timer = window.setTimeout(function () { controller.abort(); }, TIMEOUT);

    return fetch(url, { signal: controller.signal })
      .then(function (r) {
        window.clearTimeout(timer);
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .catch(function (err) {
        window.clearTimeout(timer);
        throw err;
      });
  }

  // Open-Meteo WMO weather codes, collapsed to the states that matter to
  // somebody deciding whether to treat a driveway tonight.
  function describe(code) {
    if (code === 0) return 'Clear';
    if (code <= 3) return 'Cloud';
    if (code === 45 || code === 48) return 'Fog';
    if (code >= 51 && code <= 57) return 'Drizzle';
    if (code >= 61 && code <= 67) return 'Rain';
    if (code >= 71 && code <= 77) return 'Snow';
    if (code >= 80 && code <= 82) return 'Showers';
    if (code === 85 || code === 86) return 'Snow showers';
    if (code >= 95) return 'Storm';
    return 'Unsettled';
  }

  function Weather(root) {
    this.root = root;
    this.out = root.querySelector('[data-weather-out]');
    this.place = root.querySelector('[data-weather-place]');
    this.status = root.querySelector('[data-weather-status]');
    this.locateBtn = root.querySelector('[data-weather-locate]');
    this.form = root.querySelector('[data-weather-form]');
    this.input = root.querySelector('[data-weather-input]');

    this.defaultName = root.getAttribute('data-weather-default-name') || 'Pittston, PA';
    this.lat = parseFloat(root.getAttribute('data-weather-lat'));
    this.lon = parseFloat(root.getAttribute('data-weather-lon'));

    this.auto = root.getAttribute('data-weather-auto') === 'true';
    this.ipLookup = root.getAttribute('data-weather-ip') !== 'false';

    if (this.locateBtn) {
      this.locateBtn.addEventListener('click', this.locate.bind(this));
      if (!('geolocation' in navigator)) this.locateBtn.hidden = true;
    }
    if (this.form) this.form.addEventListener('submit', this.search.bind(this));

    /* Without this the strip has no controls at all: locateBtn and form are
       null there, so a wrong IP guess could never be corrected and nothing
       could ever be saved. That, not the fuzziness of IP lookup, was the
       actual defect — an approximate answer is fine, an uncorrectable one is
       not. */
    this.placeBtn = root.querySelector('[data-weather-place-btn]');
    this.panel = root.querySelector('[data-weather-panel]');
    if (this.placeBtn && this.panel) {
      var self = this;
      this.placeBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        self.setPanel(self.panel.hidden);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !self.panel.hidden) { self.setPanel(false); self.placeBtn.focus(); }
      });
      document.addEventListener('click', function (e) {
        if (!self.panel.hidden && !root.contains(e.target)) self.setPanel(false);
      });
      this.setPanel(false);
    }

    // Render something immediately: a remembered place if there is one, the
    // plant's own coordinates otherwise. Never leave the widget empty while a
    // permission decision is outstanding.
    var saved = this.recall();
    if (saved) {
      this.hasChoice = true;
      this.load(saved.lat, saved.lon, saved.name, true);
      this.considerAutoLocate();
    } else if (this.ipLookup) {
      // Nothing remembered. Resolve from the IP address first — it needs no
      // permission, no prompt and no typing, and city-level accuracy is all a
      // forecast requires. The plant's own location renders underneath it in
      // the meantime, so the widget is never blank.
      this.load(this.lat, this.lon, this.defaultName);
      this.locateByIP();
    } else {
      this.load(this.lat, this.lon, this.defaultName);
      this.considerAutoLocate();
    }
  }

  /* Device location, without ambushing anyone.

     The browser will not hand over coordinates without the visitor's consent —
     that is not a policy decision available to this theme. What we can control
     is when the prompt happens:

     - granted : they have already said yes on this site, so no prompt appears
                 and we just use it. This is the case that makes the widget
                 feel automatic on every visit after the first.
     - prompt  : first time. We wait for the button unless the merchant has
                 explicitly turned on asking at load, because an unexplained
                 permission dialog gets dismissed and a dismissal is sticky.
     - denied  : they said no. Hide the button — offering it again is just a
                 dead control — and let the town/ZIP box do the work. */
  Weather.prototype.considerAutoLocate = function () {
    if (!('geolocation' in navigator)) return;
    var self = this;

    /* Only ever prompt somebody who has not already told us where they are.
       Having typed a town is a decision; interrupting it with a permission
       dialog on the next page load is nagging, and a dismissal would cost the
       permission permanently. The silent 'granted' path below still runs for
       them, so an existing permission keeps working. */
    var mayPrompt = function () { return self.auto && !self.hasChoice; };

    var fallback = function () {
      if (mayPrompt()) self.locate();
    };

    if (!navigator.permissions || !navigator.permissions.query) { fallback(); return; }

    try {
      navigator.permissions.query({ name: 'geolocation' }).then(function (status) {
        var apply = function () {
          if (status.state === 'granted') self.locate(true);
          else if (status.state === 'denied') { if (self.locateBtn) self.locateBtn.hidden = true; }
          else if (mayPrompt()) self.locate();
        };
        apply();
        status.onchange = apply;
      }).catch(fallback);
    } catch (e) {
      fallback();
    }
  };

  /* IP lookup. Approximate — it resolves to the visitor's ISP exit, which is
     usually the right town and occasionally the wrong end of the county. That
     is fine for "how cold is it tonight" and it costs the visitor nothing.

     If it fails for any reason (blocked, rate limited, offline) the default
     location simply stays put, and the device-location path is still offered. */
  Weather.prototype.locateByIP = function () {
    var self = this;
    fetchJSON(IPGEO)
      .then(function (d) {
        if (!d || d.success === false) throw new Error('no ip fix');
        if (typeof d.latitude !== 'number' || typeof d.longitude !== 'number') throw new Error('no coords');
        var name = d.city ? (d.city + (d.region ? ', ' + d.region : '')) : 'Your area';
        // Deliberately not persisted. An IP fix is a guess, and caching it
        // turned one bad reading — a carrier gateway in the wrong state — into
        // a permanent wrong answer that never got re-checked. Re-resolving on
        // each visit costs one request and can only improve.
        self.load(d.latitude, d.longitude, name, false, 'approx');
      })
      .catch(function () { /* keep the default location on screen */ })
      .then(function () { self.considerAutoLocate(); });
  };

  Weather.prototype.setPanel = function (open) {
    if (!this.panel || !this.placeBtn) return;
    this.panel.hidden = !open;
    this.placeBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open && this.input) this.input.focus();
  };

  Weather.prototype.recall = function () {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var p = JSON.parse(raw);
      return (typeof p.lat === 'number' && typeof p.lon === 'number') ? p : null;
    } catch (e) { return null; }
  };

  Weather.prototype.remember = function (lat, lon, name) {
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify({ lat: lat, lon: lon, name: name }));
    } catch (e) { /* private mode, storage disabled — not worth surfacing */ }
  };

  Weather.prototype.say = function (msg) {
    if (this.status) this.status.textContent = msg || '';
  };

  Weather.prototype.locate = function (silent) {
    var self = this;
    if (!silent) this.say('Asking your browser for your location…');

    navigator.geolocation.getCurrentPosition(
      function (pos) {
        self.hasChoice = true;
        self.load(pos.coords.latitude, pos.coords.longitude, 'Your location', true, 'exact');
        self.setPanel(false);
      },
      function (err) {
        // A silent attempt failing is not worth a message — the visitor never
        // asked for anything, and the default location is already on screen.
        if (silent) return;
        // With the on-load prompt enabled the visitor never asked for this, so
        // a refusal is not an error worth reporting — the IP guess is already
        // on screen and the Change location box is a tap away.
        if (self.auto && !self.hasChoice) return;
        self.say(err && err.code === 1
          ? 'No problem. Type a town or ZIP instead.'
          : 'Location unavailable. Type a town or ZIP instead.');
      },
      {
        // City-level is all a forecast needs. Skipping high accuracy avoids
        // waking the GPS radio, which is faster and easier on a phone battery.
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 600000
      }
    );
  };

  Weather.prototype.search = function (event) {
    event.preventDefault();
    var q = (this.input && this.input.value || '').trim();
    if (!q) return;
    var self = this;
    this.say('Looking up ' + q + '…');

    fetchJSON(GEO + '?count=1&language=en&format=json&name=' + encodeURIComponent(q))
      .then(function (d) {
        var hit = d && d.results && d.results[0];
        if (!hit) { self.say('No match for ' + q + '. Try a nearby town.'); return; }
        var name = hit.name + (hit.admin1 ? ', ' + hit.admin1 : '');
        self.hasChoice = true;
        self.load(hit.latitude, hit.longitude, name, true, 'exact');
        self.setPanel(false);
      })
      .catch(function () { self.say('Could not reach the lookup service. Try again in a moment.'); });
  };

  Weather.prototype.load = function (lat, lon, name, persist, precision) {
    if (isNaN(lat) || isNaN(lon)) return;
    var self = this;
    this.precision = precision || 'exact';
    this.say('Loading conditions…');

    var url = FORECAST +
      '?latitude=' + encodeURIComponent(lat) +
      '&longitude=' + encodeURIComponent(lon) +
      '&current=temperature_2m,weather_code' +
      '&daily=temperature_2m_min' +
      '&temperature_unit=fahrenheit&timezone=auto&forecast_days=2';

    fetchJSON(url)
      .then(function (d) {
        if (!d || !d.current) throw new Error('bad payload');
        if (persist) self.remember(lat, lon, name);
        self.render(d, name);
      })
      .catch(function () {
        self.fail();
      });
  };

  /* Nothing loaded. Say so plainly and clear the readout rather than leaving a
     half-rendered widget or a spinner that never resolves. The rest of the page
     — including the temperature finder — works without a forecast. */
  Weather.prototype.fail = function () {
    if (this.out) this.out.innerHTML = '';
    this.say('Conditions unavailable right now.');
  };

  Weather.prototype.render = function (d, name) {
    var now = Math.round(d.current.temperature_2m);
    var lows = (d.daily && d.daily.temperature_2m_min) || [];
    var low = lows.length ? Math.round(Math.min.apply(null, lows)) : now;
    var sky = describe(d.current.weather_code);

    if (this.place) this.place.textContent = name;
    // An approximate fix is marked so a wrong town reads as something the
    // visitor can change, rather than as the site stating it confidently.
    this.root.setAttribute('data-precision', this.precision || 'exact');
    this.say('');

    if (this.out) {
      this.out.innerHTML =
        '<span class="wx__now">' + now + '&deg;F</span>' +
        '<span class="wx__sky">' + sky + '</span>' +
        '<span class="wx__low">Low ' + low + '&deg;F</span>';
    }

    // Hand the low to anything that cares — today that is the temperature
    // finder, which uses it to start on the cold this visitor actually has.
    document.dispatchEvent(new CustomEvent('qj:forecast', {
      detail: { low: low, now: now, place: name, sky: sky }
    }));
  };

  function init(root) {
    var nodes = (root || document).querySelectorAll('[data-weather]');
    for (var i = 0; i < nodes.length; i++) new Weather(nodes[i]);
  }

  document.addEventListener('DOMContentLoaded', function () { init(); });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
